import React, { useCallback, useEffect, useState } from 'react'

import { PageTitle } from '../../../../_metronic/layout/core'
import { TablePagination } from '../../../../components/Table/TablePagination'
import { FilterManager } from '../../../common/filter/FilterManager'
import { getMerchantLookupWithToken } from '../../../common/core/_requests'
import { getErrorResponse } from '../../../common/api/errors'
import { getPaymentMethods } from '../../settings/api/getPaymentMethods'
import { getPayoutRefusals } from '../../ticket-card/core/refusalApi'
import { PayoutRefusalListItem } from '../../ticket-card/core/refusal.types'
import PayoutRefusalsTable from './components/PayoutRefusalsTable'
import {
  defaultRefusalFilters,
  MULTI_SELECT_PROPS,
  PAGE_SIZE,
  PAYOUT_REFUSAL_REASON_OPTIONS,
  PAYOUT_REFUSALS_BREADCRUMBS,
  toPayoutRefusalParams,
} from './core/consts'
import { PayoutRefusalFilterState } from './core/types'

type Option = { value: string; label: string }

const PayoutRefusalsPage: React.FC = () => {
  // Сутки считаются при открытии страницы: сброс возвращает к ним же.
  const [defaults] = useState<PayoutRefusalFilterState>(defaultRefusalFilters)
  const [items, setItems] = useState<PayoutRefusalListItem[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState<PayoutRefusalFilterState>(defaults)
  const [merchantOptions, setMerchantOptions] = useState<Option[]>([])
  const [paymentOptions, setPaymentOptions] = useState<Option[]>([])

  useEffect(() => {
    getMerchantLookupWithToken()
      .then((resp) => {
        if (resp.data?.success) setMerchantOptions(resp.data.data ?? [])
      })
      .catch(() => setMerchantOptions([]))

    getPaymentMethods()
      .then((resp: any) => {
        if (!resp?.data?.success) return
        const list = resp.data.paymentMethods ?? []
        setPaymentOptions(list.map((pm: any) => ({ value: pm.type, label: pm.type })))
      })
      .catch(() => setPaymentOptions([]))
  }, [])

  const load = useCallback(async (pg: number, filters: PayoutRefusalFilterState) => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await getPayoutRefusals(toPayoutRefusalParams(filters, pg + 1))
      if (!data.success) {
        setItems([])
        setTotalRows(0)
        setError(data.errors.general_error ?? 'Не удалось загрузить отказы')
        return
      }
      setItems(data.data ?? [])
      setTotalRows(data.totalRows ?? 0)
    } catch (e) {
      setItems([])
      setTotalRows(0)
      setError(getErrorResponse<{ general_error?: string }>(e)?.general_error ?? 'Не удалось загрузить отказы')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(0, defaults)
  }, [load, defaults])

  const handleApply = (filters: PayoutRefusalFilterState) => {
    setApplied(filters)
    setPage(0)
    load(0, filters)
  }

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(selected, applied)
  }

  return (
    <>
      <PageTitle breadcrumbs={PAYOUT_REFUSALS_BREADCRUMBS}>Отказы</PageTitle>
      <FilterManager<PayoutRefusalFilterState>
        activeFilters={[
          'dateRange',
          { filter: 'search', fieldName: 'transactionId' },
          { filter: 'selectMulti', fieldName: 'merchantTokens' },
          { filter: 'selectMulti', fieldName: 'paymentMethodTypes' },
          { filter: 'selectMulti', fieldName: 'reasons' },
          'amountRange',
        ]}
        defaultValues={defaults}
        filterProps={{
          transactionId: { label: 'ID транзакции мерчанта', placeholder: 'transactionId' },
          dateRange: { ranges: [] },
          merchantTokens: { ...MULTI_SELECT_PROPS, label: 'Мерчант', options: merchantOptions },
          paymentMethodTypes: { ...MULTI_SELECT_PROPS, label: 'Платёжный метод', options: paymentOptions },
          reasons: { ...MULTI_SELECT_PROPS, label: 'Причина', options: PAYOUT_REFUSAL_REASON_OPTIONS },
        }}
        onApplyFilters={handleApply}
      />
      <div className='card'>
        <div className='card-body'>
          {error && <div className='alert alert-danger'>{error}</div>}
          <PayoutRefusalsTable items={items} loading={loading} />
          <TablePagination
            currentPage={page}
            pageCount={Math.ceil(totalRows / PAGE_SIZE)}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  )
}

export default PayoutRefusalsPage
